import { useCallback, useState } from 'react';
import type { PaymentOption } from './payment-section/PaymentSection';
import {
  CARD,
  COMPLIMENTARY,
  DIRECT_DEBIT,
  hasStoredPaymentDetails,
  isCreditPack,
  isSingleMembership,
  newPaymentCollectorEnabled,
} from './buyMembershipRules';
import type { BuyMembershipHostData, BuyMembershipResult } from './buyMembership.types';

function renewalTermsAccepted(
  data: BuyMembershipHostData,
  accepted: boolean
): boolean {
  const plan = data.membership.planToBuy;
  return (
    (accepted || (data.flags.isFtcEnabled && !data.isCustomLink)) &&
    !isSingleMembership(plan) &&
    !isCreditPack(plan)
  );
}

function shouldUseCollector(
  data: BuyMembershipHostData,
  paymentKey: string
): boolean {
  return (
    (data.membership.planToBuy.price || 0) > 0 &&
    [CARD, DIRECT_DEBIT].includes(paymentKey) &&
    (newPaymentCollectorEnabled(data) || !hasStoredPaymentDetails(data, paymentKey))
  );
}

function purchasePaymentMethod(price: number, paymentKey: string): string {
  if (price === 0) {
    return COMPLIMENTARY;
  }
  return paymentKey === CARD ? 'credit_card' : paymentKey;
}

type PurchaseArgs = {
  data: BuyMembershipHostData;
  selectedPayment?: PaymentOption;
  selectedStartDate?: string;
  promoCode?: string;
  totalPrice: number;
  termsAccepted: boolean;
  autorenewalAccepted: boolean;
  setTermsAccepted: (accepted: boolean) => void;
  setAutorenewalAccepted: (accepted: boolean) => void;
};

type PurchaseRuntime = PurchaseArgs & {
  setErrors: (errors: string[]) => void;
  setTransactionInProgress: (inProgress: boolean) => void;
  setView: (view: 'main' | 'success' | 'error' | 'sca-pending') => void;
};

function membershipAnalytics(runtime: PurchaseRuntime) {
  return {
    membership: `${runtime.data.membership.name || ''} - ${
      runtime.data.membership.planToBuy.name || ''
    }`,
    price: runtime.totalPrice,
  };
}

function recordFailure(
  runtime: PurchaseRuntime,
  response?: BuyMembershipResult | void
) {
  runtime.data.trackPurchaseFailure(response, runtime.selectedStartDate);
  runtime.data.trackEvent({
    category: 'source:membership',
    name: 'PURCHASE_ERROR',
    payload: response?.message || 'none',
    metadata: membershipAnalytics(runtime),
  });
  runtime.setErrors(response?.errors || (response?.message ? [response.message] : []));
  runtime.setView('error');
}

function recordSuccess(
  runtime: PurchaseRuntime,
  response?: BuyMembershipResult | void
) {
  runtime.data.trackPurchaseSuccess(runtime.totalPrice, runtime.selectedStartDate);
  runtime.data.trackEvent({
    category: 'source:membership',
    name: 'PURCHASE_SUCCESS',
    value: runtime.totalPrice,
    metadata: membershipAnalytics(runtime),
  });
  runtime.setView(response?.status === 'PENDING-INTENT' ? 'sca-pending' : 'success');
}

async function createCollectorCart(
  runtime: PurchaseRuntime,
  acceptedRenewalTerms: boolean
): Promise<string | undefined> {
  const cart = await runtime.data.createCart({
    membershipId: runtime.data.membership._id || '',
    planCode: String(runtime.data.membership.planToBuy.code),
    utcStartDate: runtime.selectedStartDate
      ? runtime.data.toUtcDate(runtime.selectedStartDate)
      : undefined,
    promoCode: runtime.promoCode,
    autoRenewalTermsAccepted: acceptedRenewalTerms,
  });
  if (cart.cartId) {
    return cart.cartId;
  }
  runtime.data.showGeneralError(
    cart.message
      ? runtime.data.translate(cart.message)
      : runtime.data.translate('WEB_UNABLE_TO_CREATE_CART_FOR_PAYMENT')
  );
  return undefined;
}

async function collectPayment(
  runtime: PurchaseRuntime,
  paymentKey: string,
  acceptedRenewalTerms: boolean
) {
  const cartId = newPaymentCollectorEnabled(runtime.data)
    ? await createCollectorCart(runtime, acceptedRenewalTerms)
    : undefined;
  if (newPaymentCollectorEnabled(runtime.data) && !cartId) {
    return;
  }
  const result = await runtime.data.showPaymentCollector(paymentKey, cartId);
  if (result?.success && cartId) {
    recordSuccess(runtime, { success: true });
  }
}

function canSubmit(runtime: PurchaseRuntime, paymentKey: string): boolean {
  if (!runtime.data.user?._id) return false;
  if (!runtime.data.membership._id) return false;
  if (runtime.data.membership.planToBuy.code === undefined) return false;
  return (runtime.data.membership.planToBuy.price || 0) === 0 || Boolean(paymentKey);
}

async function submitPurchase(runtime: PurchaseRuntime) {
  const data = runtime.data;
  const plan = data.membership.planToBuy;
  const paymentKey = String(runtime.selectedPayment?.key || '').toLowerCase();
  if (!canSubmit(runtime, paymentKey)) {
    return;
  }
  const acceptedRenewalTerms = renewalTermsAccepted(
    data,
    runtime.autorenewalAccepted
  );
  runtime.setTransactionInProgress(true);
  try {
    if (shouldUseCollector(data, paymentKey)) {
      await collectPayment(runtime, paymentKey, acceptedRenewalTerms);
      return;
    }
    const result = await data.buyPlan({
      userId: data.user?._id || '',
      membershipId: data.membership._id || '',
      planCode: String(plan.code),
      paymentMethod: purchasePaymentMethod(plan.price || 0, paymentKey),
      promoCode: runtime.promoCode,
      utcStartDate: runtime.selectedStartDate
        ? data.toUtcDate(runtime.selectedStartDate)
        : undefined,
      autoRenewalTermsAccepted: acceptedRenewalTerms,
    });
    result?.success
      ? recordSuccess(runtime, result)
      : recordFailure(runtime, result);
  } catch {
    recordFailure(runtime);
  } finally {
    runtime.setTransactionInProgress(false);
  }
}

function startPurchase(runtime: PurchaseRuntime) {
  if (runtime.data.flags.isFtcEnabled && !runtime.data.isCustomLink) {
    runtime.setAutorenewalAccepted(true);
  }
  if (runtime.data.flags.isFtcEnabled && !runtime.termsAccepted) {
    runtime.data.showPurchaseTerms(() => {
      runtime.setTermsAccepted(true);
      void submitPurchase(runtime);
    });
    return;
  }
  void submitPurchase(runtime);
}

export function useBuyMembershipPurchase(args: PurchaseArgs) {
  const [transactionInProgress, setTransactionInProgress] = useState(false);
  const [view, setView] = useState<'main' | 'success' | 'error' | 'sca-pending'>('main');
  const [errors, setErrors] = useState<string[]>([]);
  const runtime = {
    ...args,
    setErrors,
    setTransactionInProgress,
    setView,
  };
  const buy = useCallback(() => startPurchase(runtime), [runtime]);

  const tryAgain = useCallback(() => {
    setErrors([]);
    setView('main');
  }, []);

  return {
    buy,
    errors,
    setView,
    transactionInProgress,
    tryAgain,
    view,
  };
}
